import isEmpty from 'lodash/isEmpty';
import * as layers from './layers';
import {
  LAYERS,
  MAX_PROPERTY_LIST_LAYER_ZOOM,
  MIN_PROPERTY_LIST_LAYER_ZOOM,
  SOURCES,
} from './map-constants';
import { addPropertyMouseEffects } from './mouse-effects';

/**
 * This class defines the all layer management responsibilities
 */
export class MapLayerMgr {
  constructor(mapInstance) {
    if (!mapInstance) {
      throw new Error(
        'MapLayerMgr cannot be instantiated without a mapInstance'
      );
    }
    this.mapInstance = mapInstance;
  }

  mapInstance = null;

  hoveredStateId = null;

  hoveredStateIds = new Set();

  data = {};
  layerIds = [];
  sourceIds = [];

  removeLayer = (layerId) => {
    if (this.mapInstance.getLayer(layerId)) {
      this.mapInstance.removeLayer(layerId);
    }
  };

  addPropertyListLayer(
    insertBefore,
    onDotClick,
    platformIcons,
    displayUnlicensedPropertiesSeparately,
    { minLayerZoom } = { minLayerZoom: MIN_PROPERTY_LIST_LAYER_ZOOM }
  ) {
    if (this.mapInstance.getLayer(LAYERS.PROPERTY_LIST)) return;
    const layer = layers.getPropertyListLayer(
      displayUnlicensedPropertiesSeparately
    );

    this.mapInstance.addLayer(layer, insertBefore);
    this.mapInstance.setLayerZoomRange(
      LAYERS.PROPERTY_LIST,
      minLayerZoom,
      MAX_PROPERTY_LIST_LAYER_ZOOM
    );
    addPropertyMouseEffects(this.mapInstance, onDotClick, platformIcons);
  }

  addPropertyClusterLayer() {
    if (
      this.mapInstance.getLayer(LAYERS.CLUSTER) ||
      this.mapInstance.getLayer(LAYERS.PROPERTY_COUNT)
    )
      return;
    this.mapInstance.addLayer(layers.clusterLayer);
    this.mapInstance.addLayer(layers.propertyCountLayer);
  }

  addBoundaryLayer(shapeFile, insertBefore) {
    if (
      this.mapInstance.getLayer(LAYERS.CITY_BOUNDARY_LINE) ||
      isEmpty(shapeFile)
    )
      return;
    this.mapInstance.addLayer(layers.boundaryLayer, insertBefore);
  }

  /**
   * Sets layerIds and sourceIds. These values keep track of
   * the layer and source ids each zone is drawn on.
   */
  getZoningLayersAndSources(zoningFile) {
    this.layerIds = zoningFile.map((_, i) => `${LAYERS.ZONING_FILE_FILL}_${i}`);
    this.sourceIds = zoningFile.map((_, i) => `${SOURCES.ZONING_FILE}_${i}`);
  }

  /**
   * Goes through all the features on the zone fill layer and maps the
   * region name to the corresponding feature id
   *
   * Example of the feature data: (each feature has the following)
   * 0
   *  id: "0"
   *  type: "Feature"
   *  properties
   *    region: "ZoneName"
   *  geometry
   *    type: "Polygon"
   *    coordinates: [array of zone's boundry coordinates]
   */
  mappingRegionToFeatureId() {
    this.mapInstance
      .queryRenderedFeatures({
        layers: this.layerIds,
      })
      .forEach((feat) => {
        this.data[`${feat.properties.region}`] = feat.id;
      });
  }

  addZoningLayer(zoningFiles, insertBefore) {
    if (!zoningFiles) return;
    zoningFiles.forEach((_, i) => {
      if (this.mapInstance.getLayer(`${LAYERS.ZONING_FILE_FILL}_${i}`)) return;

      this.mapInstance.addLayer(layers.zoningFillLayer(i), insertBefore);
      this.mapInstance.addLayer(layers.zoningBoundaryLayer(i), insertBefore);

      // maps the features (region and ids) only once when map is first loaded
      this.mapInstance.once('idle', () => {
        this.getZoningLayersAndSources(zoningFiles);
        this.mappingRegionToFeatureId();
      });

      // moves moves on the map zone layer, hoverstate set to true
      this.mapInstance.on(
        'mousemove',
        `${LAYERS.ZONING_FILE_FILL}_${i}`,
        (e) => {
          if (e.features.length > 0) {
            if (this.hoveredStateId && this.hoveredStateId !== 0) {
              this.mapInstance.setFeatureState(
                {
                  source: `${SOURCES.ZONING_FILE}_${i}`,
                  id: this.hoveredStateId,
                },
                { hover: false }
              );
            }

            /**
             * The key "id" does not exist at the specified level in the Imperial Beach Dummy
             * data so we default it to the current looping index
             */
            this.hoveredStateId = e.features[0].id || i;

            // check so updates DOM w/ same value only once
            if (!this.hoveredStateIds.has(this.hoveredStateId)) {
              this.hoveredStateIds.add(this.hoveredStateId);

              const infoBoxElem = document.querySelector(
                '.hoa-infobox-content-wrapper'
              );
              const zoneNameElem = infoBoxElem.querySelector(
                '.hoa-infobox-zone-name'
              );
              const zoneName = e.features[0].properties.region;

              infoBoxElem.style.display = 'block';
              zoneNameElem.innerHTML = zoneName;
            }

            this.mapInstance.setFeatureState(
              {
                source: `${SOURCES.ZONING_FILE}_${i}`,
                id: this.hoveredStateId,
              },
              { hover: true }
            );
          }
        }
      );

      // mouse moves off the map zone layer, hover state is set to false
      this.mapInstance.on(
        'mouseleave',
        `${LAYERS.ZONING_FILE_FILL}_${i}`,
        () => {
          this.hoveredStateIds.forEach((idx) => {
            this.mapInstance.setFeatureState(
              { source: `${SOURCES.ZONING_FILE}_${i}`, id: idx },
              { hover: false }
            );
          });
          this.hoveredStateIds.clear();
          this.hoveredStateId = null;
          const infoBoxElem = document.querySelector(
            '.hoa-infobox-content-wrapper'
          );
          infoBoxElem.style.display = 'none';
        }
      );
    });
  }

  /**
   * Resets the fill-color for all the zone features on the map.
   */
  resetZoneFeatureColor() {
    for (const key in this.data) {
      this.mapInstance.setFeatureState(
        { source: this.sourceIds, id: this.data[key] },
        { selected: false }
      );
    }
  }

  /**
   * For a given zone name, finds the corresponding id and
   * sets the feature state for the corresponding zone feature.
   *
   * this.data is structures in the following way (name: id)
   * {
   *  zoneName1: featureID
   *  zoneName2: featureID
   *  ...
   * }
   * this.sourceIds is an array of all source ids for zoning layer
   *
   * TODO: look into keeping track of all the zones already selected
   * so you do not have to go through each zone every time
   */
  changeZonesFeatureColor(filteredZones) {
    this.resetZoneFeatureColor();
    filteredZones.forEach((zone) => {
      const zoneIndex = this.data[zone];
      this.mapInstance.setFeatureState(
        { source: this.sourceIds, id: zoneIndex },
        { selected: true }
      );
    });
  }
}
